import { ReactComponent as IconDownload } from "@/assets/svgs/download-white.svg";
import { ReactComponent as IconPrinter } from "@/assets/svgs/icon-printer.svg";
import { ReactComponent as IconQrCode } from "@/assets/svgs/qr-code.svg";
import { ReactComponent as IconPrevious } from '@/assets/svgs/transactions-back-icon.svg';
import { ReactComponent as IconBadgeFilled } from '@/assets/svgs/icon-info-filled.svg';
import Button from '@/components/Button';
import Portal from '@/components/Portal';
import ViewActivityLogModal from '@/components/ViewActivityLogModal/index';
import useEtrDocument from "@/hooks/use-etr-document/index";
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import { useNavigate, useParams } from '@/lib/router-dom';
import { clearTTDataAndFile } from '@/redux/actions/trade-trust';
import FileSaver from "file-saver";
import JSZip from "jszip";
import moment from "moment";
import QRCode from "qrcode.react";
import { forwardRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import IframeDetails from '../e-transferable-record/components/iframe-details/iframeDetails';
import RecordDetailsSidePanel from '../record-details-side-panel/index';
import ETRFileUploadComponent from './etr-attachments.jsx';
import Popover from "@/components/Popover/index";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature.js";
import { registry } from "./templates/index";
import useConfig from '@/hooks/use-config';
import api from '@/services/api/etr.js';
import { useFeature } from "@/hooks/use-feature/index";
import { renderUserEmailInline } from '@/utils/formatUserEmail';

const progressEnum = ["ISSUING", "SURRENDERING", "SHREDDING", "ISSUANCE_RETRY", "RESTORING"];
const pendingApprovalEnum = ["HOLDER_TRANSFER_PENDING_APPROVAL", "OWNER_TRANSFER_PENDING_APPROVAL", "OWNER_HOLDER_TRANSFER_PENDING_APPROVAL", "NOMINATION_PENDING_APPROVAL", "SURRENDER_PENDING_APPROVAL", "OWNER_HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL", "HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL", "OWNER_TRANSFER_REJECTION_PENDING_APPROVAL"];
const successEnum = ["ISSUED", "SURRENDERED", "SHREDDED", "RESTORED", "FILE_UPLOAD"];
const failEnum = ["ISSUANCE_FAILED", "SURRENDERING_FAILED", "SHREDDING_FAILED", "RESTORATION_FAILED"];
const rejectedEnum = ["SURRENDER_REJECTED", "OWNER_TRANSFER_REJECTED", "HOLDER_TRANSFER_REJECTED", "OWNER_HOLDER_TRANSFER_REJECTED", "NOMINATION_REJECTED", "OWNER_TRANSFER_REJECTION_REJECTED", "OWNER_HOLDER_TRANSFER_REJECTION_REJECTED", "HOLDER_TRANSFER_REJECTION_REJECTED"];
const updatedEnum = ["UPDATED"];
const discardedEnum = ["OWNER_TRANSFER_DISCARDED", "HOLDER_TRANSFER_DISCARDED", "OWNER_HOLDER_TRANSFER_DISCARDED", "NOMINATION_DISCARDED", "SURRENDER_DISCARDED", "OWNER_HOLDER_TRANSFER_REJECTION_DISCARDED", "HOLDER_TRANSFER_REJECTION_DISCARDED", "OWNER_TRANSFER_REJECTION_DISCARDED"];

const makerCheckerEnums = {
    progress: ["OWNER_TRANSFERRING", "HOLDER_TRANSFERRING", "OWNER_HOLDER_TRANSFERRING", "NOMINATING", "OWNER_TRANSFER_APPROVED", "HOLDER_TRANSFER_APPROVED", "OWNER_HOLDER_TRANSFER_APPROVED", "NOMINATION_APPROVED", "ENDORSEMENT_APPROVED", "SURRENDER_APPROVED", "HOLDER_TRANSFER_REJECTION_IN_PROGRESS", "OWNER_HOLDER_TRANSFER_REJECTION_IN_PROGRESS", "OWNER_TRANSFER_REJECTION_IN_PROGRESS"],
    success: ["HOLDER_TRANSFERRED", "OWNER_TRANSFERRED", "OWNER_HOLDER_TRANSFERRED", "NOMINATED", "HOLDER_TRANSFER_REJECTION_SUCCESS", "OWNER_HOLDER_TRANSFER_REJECTION_SUCCESS", "OWNER_TRANSFER_REJECTION_SUCCESS"],
    fail: ["OWNER_TRANSFERRING_FAILED", "HOLDER_TRANSFERRING_FAILED", "OWNER_HOLDER_TRANSFERRING_FAILED", "NOMINATING_FAILED", "ENDORSEMENT_FAIL", "OWNER_TRANSFER_REJECTION_FAILED", "OWNER_HOLDER_TRANSFER_REJECTION_FAILED", "HOLDER_TRANSFER_REJECTION_FAILED"]
};

const etrProcesses = {
    FILE_UPLOAD: ["FILE_UPLOAD"],
    ISSUANCE: ["ISSUING", "ISSUED", "ISSUANCE_FAILED", "ISSUANCE_RETRY"],
    OWNER_TRANSFER: ["OWNER_TRANSFER_PENDING_APPROVAL", "OWNER_TRANSFERRED", "OWNER_TRANSFERRING_FAILED", "OWNER_TRANSFER_REJECTED", "OWNER_TRANSFERRING", "OWNER_TRANSFER_APPROVED", "ENDORSEMENT_PENDING_APPROVAL", "ENDORSEMENT_REJECTED", "ENDORSEMENT_APPROVED", "ENDORSEMENT_FAIL", "OWNER_TRANSFER_DISCARDED"],
    HOLDER_TRANSFER: ["HOLDER_TRANSFER_PENDING_APPROVAL", "HOLDER_TRANSFERRED", "HOLDER_TRANSFERRING_FAILED", "HOLDER_TRANSFER_REJECTED", "HOLDER_TRANSFERRING", "HOLDER_TRANSFER_APPROVED", "HOLDER_TRANSFER_DISCARDED"],
    OWNER_HOLDER_TRANSFER: ["OWNER_HOLDER_TRANSFER_PENDING_APPROVAL", "OWNER_HOLDER_TRANSFERRED", "OWNER_HOLDER_TRANSFERRING_FAILED", "OWNER_HOLDER_TRANSFER_REJECTED", "OWNER_HOLDER_TRANSFERRING", "OWNER_HOLDER_TRANSFER_APPROVED", "OWNER_HOLDER_TRANSFER_DISCARDED"],
    NOMINATION: ["NOMINATING", "NOMINATED", "NOMINATING_FAILED", "NOMINATION_PENDING_APPROVAL", "NOMINATION_REJECTED", "NOMINATION_APPROVED", "NOMINATION_DISCARDED"],
    SURRENDER: ["SURRENDERING", "SURRENDERED", "SURRENDERING_FAILED", "SURRENDER_APPROVED", "SURRENDER_PENDING_APPROVAL", "SURRENDER_REJECTED", "SURRENDER_DISCARDED"],
    SHRED: ["SHREDDING", "SHREDDED", "SHREDDING_FAILED"],
    RESTORE: ["RESTORING", "RESTORED", "RESTORATION_FAILED"],
    OWNER_HOLDER_TRANSFER_REJECTION: ["OWNER_HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL", "OWNER_HOLDER_TRANSFER_REJECTION_REJECTED", "OWNER_HOLDER_TRANSFER_REJECTION_IN_PROGRESS", "OWNER_HOLDER_TRANSFER_REJECTION_SUCCESS", "OWNER_HOLDER_TRANSFER_REJECTION_APPROVED", "OWNER_HOLDER_TRANSFER_REJECTION_FAILED", "OWNER_HOLDER_TRANSFER_REJECTION_DISCARDED"],
    OWNER_TRANSFER_REJECTION: ["OWNER_TRANSFER_REJECTION_PENDING_APPROVAL", "OWNER_TRANSFER_REJECTION_REJECTED", "OWNER_TRANSFER_REJECTION_IN_PROGRESS", "OWNER_TRANSFER_REJECTION_SUCCESS", "OWNER_TRANSFER_REJECTION_APPROVED", "OWNER_TRANSFER_REJECTION_FAILED", "OWNER_TRANSFER_REJECTION_DISCARDED"],
    HOLDER_TRANSFER_REJECTION: ["HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL", "HOLDER_TRANSFER_REJECTION_REJECTED", "HOLDER_TRANSFER_REJECTION_IN_PROGRESS", "HOLDER_TRANSFER_REJECTION_SUCCESS", "HOLDER_TRANSFER_REJECTION_APPROVED", "HOLDER_TRANSFER_REJECTION_FAILED", "HOLDER_TRANSFER_REJECTION_DISCARDED"],
};

const getCombinedEnum = (baseEnum, makerCheckerEnum) => {
    return [...baseEnum, ...makerCheckerEnum];
};

const getProcessedType = (type, alternateProcessType) => {
    const isETRIGPNITRV5FeatureEnabled = feature(featureConstant?.ETR_IGPNI_TR_V5);
    const getFormattedText = (processKey) => {
        /** process names - following alternate name convention - custom text */
        if (["RESTORE", "FILE_UPLOAD", "HOLDER_TRANSFER", "OWNER_TRANSFER", "OWNER_HOLDER_TRANSFER", "NOMINATION", "SURRENDER", "ISSUANCE", "SHRED", "OWNER_HOLDER_TRANSFER_REJECTION", "HOLDER_TRANSFER_REJECTION", "OWNER_TRANSFER_REJECTION"].includes(processKey)) {
            switch (processKey) {
                case "RESTORE":
                    return isETRIGPNITRV5FeatureEnabled ? "Record surrender rejection" : "Surrender rejection";
                case "FILE_UPLOAD":
                    return "Record upload";
                case "HOLDER_TRANSFER":
                    return "Holdership transfer";
                case "OWNER_TRANSFER":
                    return "Ownership transfer";
                case "OWNER_HOLDER_TRANSFER":
                    return "Ownership & Holdership transfer";
                case "NOMINATION":
                    return "Ownership nomination";
                case "SURRENDER":
                    return "Record surrender";
                case "ISSUANCE":
                    return "Record issuance";
                case "SHRED":
                    return "Record shredding";
                case "OWNER_HOLDER_TRANSFER_REJECTION":
                    return "Ownership and Holdership transfer rejection"
                case "HOLDER_TRANSFER_REJECTION":
                    return "Holdership transfer rejection"
                case "OWNER_TRANSFER_REJECTION":
                    return "Ownership transfer rejection"
                default:
                    return null;
            }
        } else {
            /** process names - following standard name convention (convert enum key - capitalize first letters & replace _ with space) */
            return processKey.replace('_', ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
        }
    };

    const processType = (type) => {
        for (const [key, values] of Object.entries(etrProcesses)) {
            if (values.includes(type)) {
                return getFormattedText(key);
            }
        }
        return null;
    };

    let typeToProcess = updatedEnum.includes(type) ? alternateProcessType : type;
    return processType(typeToProcess);
};

const getIconAndMessage = (action, process) => {
    /** define different icons here if required */
    const iconType = {
        pending: <IconBadgeFilled />,
        success: <IconBadgeFilled />,
        fail: <IconBadgeFilled />,
        reject: <IconBadgeFilled />,
        progress: <IconBadgeFilled />,
        update: <IconBadgeFilled />,
        discard: <IconBadgeFilled />
    };

    const baseEnums = {
        progress: progressEnum,
        success: successEnum,
        fail: failEnum,
        pending: pendingApprovalEnum,
        reject: rejectedEnum,
        update: updatedEnum,
        discard: discardedEnum
    };

    const makerCheckerEnumsMap = {
        progress: makerCheckerEnums.progress,
        success: makerCheckerEnums.success,
        fail: makerCheckerEnums.fail
    };

    const combinedEnums = {};
    for (const [key, baseEnum] of Object.entries(baseEnums)) {
        combinedEnums[key] = makerCheckerEnumsMap[key]
            ? getCombinedEnum(baseEnum, makerCheckerEnumsMap[key])
            : baseEnum;
    }

    const messages = {
        pending: `${process} is pending approval`,
        success: `${process} was successful`,
        fail: `${process} was unsuccessful`,
        reject: `${process} was denied`,
        progress: `${process} is in progress`,
        update: `${process} was updated`,
        discard: `${process} request was discarded`
    };

    for (const [key, enumArray] of Object.entries(combinedEnums)) {
        if (enumArray.includes(action)) {
            return { icon: iconType[key], message: messages[key] };
        }
    }
    return null;
};

export const getEtrStatus = (action, eventStatus) => {
    if (!action) return null;

    const etrProcess = getProcessedType(action, eventStatus);
    return getIconAndMessage(action, etrProcess);
};

const RecordTransferDetailsPage = () => {
    const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
    const dispatch = useDispatch()
    const ttData = useSelector((state) => state.tt);
    const [ttFileName, setTTFileName] = useState();
    const iframeRef = useRef();
    const iframeRefPrint = useRef();
    const attachmentRef = useRef();
    const { renderUrl, docData, ttDocument } = ttData?.data ?? {};
    const qrcodeUrl = docData?.links?.self?.href;
    const [qrCodePopover, setQrCodePopover] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [fileLists, setFileLists] = useState([]);
    const [elementSchema, setElementSchema] = useState({});

    /*=================================================
        API
    /================================================*/
    const { config } = useConfig()

    const [loading, setLoading] = useState(false);
    const isIGPNILegalRequirementsFeatureEnabled = feature(featureConstant?.IG_PNI_LEGAL_REQUIREMENTS);
    const { goTo } = useNavigate();
    const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: "E-Transferable Record",
		breadcrumbs: [
            {
                label: '',
                link: '/'
            },
            {
                label: 'E-Transferable Record',
                link: '/record-transfer'
            }
		]
	});

    const SELECTED_TAB = { ASSET_DATA: "assetData", ATTACHMENTS: "attachments" };
    const [selectedTabForView, setSelectedTabForView] = useState(SELECTED_TAB.ASSET_DATA);

    const { transferableRecordId } = useParams()
    const {
        etrDocErrorMsg,
        fetchEtrDocumentDetails,
        etrDocDetails,
        etrDocListLoading,
        getActivityLog,
        activityLog,
        fetchEtrDocumentBlockchainDetails,
        etrDocBlockchainDetails,
        etrDocBlockchainDetailsLoading,
        fetchEtrDocumentEndorsechainDetails,
        etrDocEndorsechainDetails,
        etrDocEndorsechainDetailsLoading
    } = useEtrDocument();

    // Activity Log Modal
    const { visible: visibleViewRequestLog, hide: hideViewRequestLog, show: showViewRequestLog } = useModal();

    useEffect(() => {
        setTTFileName(undefined);
        return () => {
            dispatch(clearTTDataAndFile());
        }
    }, []);

    useEffect(() => {
        if (ttDocument) {
            const blob = new Blob([JSON.stringify(ttDocument)], { type: "text/json" });
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            return () => {
                //release blob url to avoid memory leak
                URL.revokeObjectURL(url);
            };
        }
    }, [ttDocument]);

    function handleDownload(selectedTabForView) {
        if (selectedTabForView === SELECTED_TAB.ASSET_DATA) {
            const blob = new Blob([JSON.stringify(ttDocument)], { type: "text/json" });
            FileSaver.saveAs(blob, etrDocDetails?.name.includes(".tt") ? etrDocDetails?.name : (etrDocDetails?.name + ".tt"));
        } else if (selectedTabForView === SELECTED_TAB.ATTACHMENTS) {
            if (fileLists.length === 0) return;

            const zip = new JSZip();
            fileLists.forEach((file) => {
                const { data, name } = file;
                zip.file(name, data, { base64: true });
            })
            zip.generateAsync({ type: "blob" }).then(function (cont) {
                FileSaver.saveAs(cont, "attachments.zip");
            });
        }
    }

    useEffect(() => {
        if (docData?.attachments?.length > 0) {
            const attachments = docData.attachments?.map((item) => {
                return {
                    data: item?.data,
                    // v2 tt document has filename, v3 tt document has fileName
                    name: item?.filename ?? item?.fileName,
                    mimeType: item?.mimeType,
                }
            })
            setFileLists(attachments);
        }
    }, [docData?.attachments]);

    useEffect(() => {
		if (etrDocDetails?.name) {
			setBreadcrumbsItems([
				{
					label: '',
					link: '/'
				 },
				{
					label: `E-Transferable Record`,
					link: '/record-transfer'
				},
				{
					label: etrDocDetails?.name,
					link:''
				}
			])
		}
        const dataElementSchema = config?.consumes?.find(s => s.id.toLowerCase() === etrDocDetails?.templateName.toLowerCase());
        setElementSchema(dataElementSchema);
    }, [etrDocDetails])

    /*=================================================
        Routes
    /================================================*/
    function goBack() {
        return goTo('/record-transfer')
    }

    /*=================================================
        API Calls
    /================================================*/
    useEffect(() => {
        dispatch(clearTTDataAndFile())
        fetchEtrDocumentDetails(transferableRecordId);
        getActivityLog(transferableRecordId);
    }, [transferableRecordId]);

    const handlePrint = () => {
        if (selectedTabForView === SELECTED_TAB.ASSET_DATA) {
            iframeRef.current?.handlePrint();
        } else if (selectedTabForView === SELECTED_TAB.ATTACHMENTS) {
            attachmentRef?.current?.handlePrint();
        }
    };

    const handleClick = async () => {
        await fetchEtrDocumentEndorsechainDetails(transferableRecordId);
        setLoading(true);
        iframeRefPrint.current?.handleClick(() => {
            api.updateAuditTrail();
            setLoading(false);
        });
    };

    const getAction = (activityLog) => {
        if (!activityLog || activityLog.length === 0) return null;
        return activityLog[0]?.action === "DATA_ELEMENT_SHARED" ? activityLog[1]?.action : activityLog[0]?.action;
    };

    const getEventStatus = (activityLog) => {
        if (!activityLog || activityLog.length === 0) return null;
        return activityLog[0]?.action === "DATA_ELEMENT_SHARED" ? activityLog[1]?.transferableRecord?.eventStatus : activityLog[0]?.transferableRecord?.eventStatus;
    };

    const templateProps = {
        document: etrDocDetails?.ttData?.docData?.credentialSubject,
        logo: etrDocDetails?.ttData?.renderUrl.split("logoID=")[1],
        endorsementChainLists: etrDocEndorsechainDetails?.endorsementChain,
        issuer: etrDocDetails?.issuer?.name,
        owner: etrDocBlockchainDetails?.owner,
        holder: etrDocBlockchainDetails?.holder,
        created: etrDocDetails?.created,
        tokenRegistryVersion: etrDocDetails?.tokenRegistryVersion,
        tokenRegistryAddress: etrDocDetails?.ttData?.tokenRegistryAddress,
        merkleRoot: etrDocDetails?.ttData?.ttDocument?.proof?.merkleRoot,
        elementSchema: elementSchema?.schema,
        fileName: etrDocDetails?.name
    };

    const TemplateRenderer = forwardRef(({ templateId }, ref) => {
        const TemplateComponent = registry[templateId];
        return (
            <div>
                <div style={{ width: "100%" }}>
                    {TemplateComponent && (<TemplateComponent {...templateProps} ref={ref} />)}
                </div>
            </div>
        );
    });

    return (
        <>
            <Breadcrumbs breadcrumbItems={breadcrumbsItems} />

            <div className="title-header transactions-eform-heading__bottom">
                <div className="transaction-title-header__left">
                    <span className="transaction-title-header__title__status--row">

                        <div className='transaction-title-header__heading'>
                            {etrDocDetails?.name}
                        </div>
                    </span>
                </div>
                <div className="transaction-title-header__right">
                    <a
                        download={etrDocDetails?.name.includes(".tt") ? etrDocDetails?.name : (etrDocDetails?.name + ".tt")}
                        target="_black"
                        href={downloadUrl}
                        role="button"
                    >
                        <Button round
                            full maxWidth={140} text={"Download .TT File"}
                        /></a>
                </div>
            </div>

            <div className="etr-issuance__details">
                <div className="etr-issuance__details__row">
                    <div className="etr-issuance__details__left">
                        <div>
                            {getEtrStatus(activityLog?.[0]?.action === "DATA_ELEMENT_SHARED" ? activityLog?.[1]?.action : activityLog?.[0]?.action)?.icon}
                        </div>
                        <div>
                            <div className='etr-issuance__record-status'>
                                {getEtrStatus(getAction(activityLog), getEventStatus(activityLog))?.message}
                            </div>
                            <div className='etr-issuance__record-date'>{moment(activityLog?.[0]?.action === "DATA_ELEMENT_SHARED" ? activityLog?.[1]?.created : activityLog?.[0]?.created ?? etrDocDetails?.created).format("DD/MM/YYYY h:mm A")}</div>
                        </div>
                    </div>
                    <div className="etr-issuance__details__right"
                        onClick={() => showViewRequestLog()}
                        onKeyDown={() => showViewRequestLog()}
                        role="none">
                        View Activity Log
                    </div>
                </div>
                {etrDocErrorMsg && <div className="etr-issuance__record-error-msg">
                    <div className="etr-issuance__record-error-msg-bold">
                        Note:
                    </div>
                    Unable to retrieve blockchain information for now. You may proceed by retry via “Retry” button.
                </div>
                }
                {activityLog?.[0]?.action.includes('FAILED') && <div className="etr-issuance__record-error-msg">
                    <div className="etr-issuance__record-error-msg-bold">
                        Note:
                    </div>
                    <div className="etr-issuance__record-error-msg-details">
                        Blockchain update failed. You may proceed by either retry or discard this attempt via “Retry” or “Discard Request” buttons respectively.
                    </div>
                </div>

                }
                {activityLog?.[0]?.action.includes('REJECTED') && <div className="etr-issuance__record-error-msg">
                    <div className="etr-issuance__record-error-msg-bold">
                        Reason for rejection:
                    </div>
                    <div className="etr-issuance__record-error-msg-details">
                        {activityLog?.[0]?.transactionRequestLog?.reason}
                    </div>
                    <div className="etr-issuance__record-error-msg-user-details">
                        From: {isSoftDeleteUserEnabled ? renderUserEmailInline(activityLog?.[0]?.email) : activityLog?.[0]?.email}
                    </div>
                </div>
                }
            </div>


            <div className='etr-data-element-payload__eform-data'>
                <div className="etr-data-element-payload__eform">
                    <div className='etr-data-element-payload__eform-heading'>
                        <div className="left">
                            <div className='etr-data-element-payload-review-status-name'>
                                {!!ttFileName && ttFileName + ":"}
                            </div>
                            <div
                                className={selectedTabForView === SELECTED_TAB.ASSET_DATA ? `etr-data-element-payload-review-status-active` : `etr-data-element-payload-review-status`}
                                onClick={() => setSelectedTabForView(SELECTED_TAB.ASSET_DATA)}
                                onKeyDown={() => setSelectedTabForView(SELECTED_TAB.ASSET_DATA)}
                                role="none"
                            >
                                Asset Data
                            </div>
                            <div
                                className={selectedTabForView === SELECTED_TAB.ATTACHMENTS ? `etr-data-element-payload-review-status-active` : `etr-data-element-payload-review-status`}
                                onClick={() => setSelectedTabForView(SELECTED_TAB.ATTACHMENTS)}
                                onKeyDown={() => setSelectedTabForView(SELECTED_TAB.ATTACHMENTS)}
                                role="none"
                            >
                                Attachments
                            </div>
                        </div>
                        <div className="right">
                            {(renderUrl && (
                                (selectedTabForView === SELECTED_TAB.ASSET_DATA) ||
                                (selectedTabForView === SELECTED_TAB.ATTACHMENTS && fileLists.length > 0)
                            ) && (
                                    <>
                                        {qrcodeUrl && (
                                            <div className="button_container__button">
                                                <Popover visible={qrCodePopover} className=""
                                                    options={{ modifiers: [{ name: 'offset', options: { offset: [25, 10] } }] }}
                                                >
                                                    <Popover.Trigger>
                                                        <div className="qr_code__relative" data-testid="qr_code__relative"
                                                            onClick={() => {
                                                                setQrCodePopover(!qrCodePopover);
                                                            }}
                                                        >
                                                            <IconQrCode />
                                                        </div>
                                                    </Popover.Trigger>
                                                    <Popover.Content>
                                                        <div data-testid="qr-code-svg" className={qrCodePopover ? "qr_code qr_code__block" : "qr_code qr_code__hidden"}>
                                                            <QRCode
                                                                value={qrcodeUrl}
                                                                level="Q"
                                                                size={200}
                                                                bgColor="#FFFFFF"
                                                                fgColor="#000000"
                                                                imageSettings={{
                                                                    src: `/img/logo-qrcode.png`,
                                                                    height: 50,
                                                                    width: 55,
                                                                    excavate: true,
                                                                }}
                                                                onClick={() => {
                                                                    window.open(qrcodeUrl, "_blank");
                                                                }}
                                                            />
                                                        </div>
                                                    </Popover.Content>
                                                </Popover>
                                            </div>
                                        )}
                                        {isIGPNILegalRequirementsFeatureEnabled ? <div className="button_container__button">
                                            <span
                                                className="print__icon"
                                                data-testid="print__icon"
                                                onClick={() => handleClick()}
                                                role="none"
                                            >
                                                <IconPrinter />
                                            </span>
                                        </div> : <div>
                                            <div className="button_container__button">
                                                <span
                                                    className="print__icon"
                                                    data-testid="print__icon"
                                                    onClick={() => handlePrint()}
                                                >
                                                    <IconPrinter />
                                                </span>
                                            </div>
                                            <div className="button_container__button">
                                                <a
                                                    role="button"
                                                    data-testid="download__icon"
                                                    onClick={() => handleDownload(selectedTabForView)}
                                                    data-tooltip-id="tooltip"
                                                    data-tooltip-content={selectedTabForView === SELECTED_TAB.ASSET_DATA ? `Download .TT` : `Download All Attachments`}
                                                >
                                                    <IconDownload />
                                                </a>
                                            </div>
                                        </div>}
                                    </>
                                )
                            )}
                        </div>
                    </div>
                    {selectedTabForView === SELECTED_TAB.ASSET_DATA && <div className='etr-issuance-eform-data'>
                        <IframeDetails
                            seTTFileName={setTTFileName}
                            ref={iframeRef}
                        />
                        <div style={{ display: "none" }}>
                            <TemplateRenderer
                                templateId={etrDocDetails?.templateName}
                                ref={iframeRefPrint}
                            />
                        </div>
                    </div>}
                    {selectedTabForView === SELECTED_TAB.ATTACHMENTS && <div className='etr-issuance-eform-data'>
                        <div className="etr-eform-row-heading">Attachment</div>
                        <ETRFileUploadComponent
                            ref={attachmentRef}
                            fileLists={fileLists}
                            isReadOnly={true}
                        />
                    </div>}
                </div>
                <RecordDetailsSidePanel
                    etrDocDetails={etrDocDetails}
                    fetchEtrDocumentBlockchainDetails={fetchEtrDocumentBlockchainDetails}
                    etrDocBlockchainDetails={etrDocBlockchainDetails}
                    etrDocBlockchainDetailsLoading={etrDocBlockchainDetailsLoading}
                    etrDocErrorMsg={etrDocErrorMsg}
                />
            </div >

            <ViewActivityLogModal
                visible={visibleViewRequestLog}
                close={hideViewRequestLog}
                activityLog={activityLog}
                activityLogType={"etr"}
                etrDocDetails={etrDocDetails}
            />

            {
                (etrDocListLoading || etrDocBlockchainDetailsLoading || etrDocEndorsechainDetailsLoading || loading) && (
                    ReactDOM.createPortal(
                        <div className='modal white'>
                            <div className='loading'>
                                <img src="/loading.gif" alt="loader" />
                            </div>
                        </div>
                        , document.body
                    )
                )
            }
        </>
    )
}

export default RecordTransferDetailsPage;
